import { useMemo } from "react";
import { useAppDispatch, useAppSelector } from "../../../../app/store";
import InternalReportTable, {
  ReportTableRow,
} from "../../../../components/ui/reportTable";
import {
  formatDisplayDate,
  lifeToDateWindowForMonth,
  monthEndDisplayDate,
  monthLabelToDateWindow,
  shortMonthLabel,
} from "../../../../lib/utils";
import {
  borrowalVsNonBorrowalBucketId,
  selectBorrowalVsNonBorrowalBucketLabelById,
  selectBorrowalVsNonBorrowalBuckets,
  selectBorrowalVsNonBorrowalMetricSummary,
  selectBorrowalVsNonBorrowalReportName,
  selectBorrowalVsNonBorrowalSelectedPeriod,
  selectBorrowalVsNonBorrowalStatus,
  selectBorrowalVsNonBorrowalTotals,
} from "../../logic/borrowalVsNonBorrowalReportSlice";
import { fetchBorrowalVsNonBorrowalReportThunk } from "../../logic/borrowalVsNonBorrowalReportThunk";
import {
  BorrowalVsNonBorrowalTableRow,
  DEFAULT_REPORT_AMOUNT_UNIT,
  toDisplayAmount,
} from "../../../../api/tenantInternalReports";
import MonthYearPicker from "../../../../components/ui/calendar/MonthYearPicker";
import InternalReportMetaHeader from "../common/InternalReportMetaHeader";
import InternalReportTableShimmer from "../common/InternalReportTableShimmer";
import {
  borrowalVsNonBorrowalMergedHeadersSets,
  buildBorrowalVsNonBorrowalColumns,
  metricColumns,
  MetricRow,
} from "./columns";



const BorrowalVsNonBorrowalReport: React.FC = () => {
  const dispatch = useAppDispatch();
  const buckets = useAppSelector(selectBorrowalVsNonBorrowalBuckets);
  const totals = useAppSelector(selectBorrowalVsNonBorrowalTotals);
  const reportName = useAppSelector(selectBorrowalVsNonBorrowalReportName);
  const bucketLabelById = useAppSelector(selectBorrowalVsNonBorrowalBucketLabelById);
  const selectedPeriod = useAppSelector(selectBorrowalVsNonBorrowalSelectedPeriod);
  const metricSummary = useAppSelector(selectBorrowalVsNonBorrowalMetricSummary);
  const isFetching = useAppSelector(selectBorrowalVsNonBorrowalStatus) === "loading";

  const { currentWindow, cumulativeWindow } = useMemo(
    () => ({
      currentWindow: selectedPeriod ? monthLabelToDateWindow(selectedPeriod) : null,
      cumulativeWindow: selectedPeriod
        ? lifeToDateWindowForMonth(selectedPeriod)
        : null,
    }),
    [selectedPeriod]
  );

  const overviewRows = useMemo<ReportTableRow<BorrowalVsNonBorrowalTableRow>[]>(
    () => {
      const data_rows: ReportTableRow<BorrowalVsNonBorrowalTableRow>[] =
        buckets.map((bucket, index) => ({
          key: borrowalVsNonBorrowalBucketId(bucket.amount_bucket),
          data: {
            amount_bucket_label:
              bucketLabelById[borrowalVsNonBorrowalBucketId(bucket.amount_bucket)] ??
              "—",
            data_by_date_windows: bucket.data_by_date_windows,
          },
          rowCellClassName: `border-b ${
            index === buckets.length - 1
              ? "border-b-[#969696]"
              : "border-b-[#E5E5E5]"
          }`,
        }));

      const totals_row: ReportTableRow<BorrowalVsNonBorrowalTableRow> = {
        key: "total",
        data: { amount_bucket_label: "Total", data_by_date_windows: totals },
        rowCellClassName:
          "border-t border-b border-t-[#969696] border-b-[#969696] font-bold",
      };

      return [...data_rows, totals_row];
    },
    [buckets, totals, bucketLabelById]
  );

  if (!selectedPeriod || !currentWindow || !cumulativeWindow) return null;

  const handlePeriodChange = (period: string) =>
    dispatch(fetchBorrowalVsNonBorrowalReportThunk({ period }));

  const metaItems = [
    { label: "Report Name", value: reportName ?? "-" },
    { label: "As On Date", value: monthEndDisplayDate(selectedPeriod) },
    { label: "Report Date", value: formatDisplayDate(new Date()) },
    { label: "Amount Unit", value: DEFAULT_REPORT_AMOUNT_UNIT.label },
  ];

  const metrics: MetricRow[] = metricSummary
    ? [
        {
          label: `Total Fraud Cases as on ${monthEndDisplayDate(selectedPeriod)}`,
          value: metricSummary.totalCases,
        },
        {
          label: `Total Fraud Amount (${DEFAULT_REPORT_AMOUNT_UNIT.label})`,
          value: toDisplayAmount(metricSummary.totalAmount),
        },
      ]
    : [];

  const overviewColumns = buildBorrowalVsNonBorrowalColumns(
    shortMonthLabel(selectedPeriod),
    currentWindow,
    cumulativeWindow,
  );

  return (
    <div className="flex w-full flex-col gap-7">
      <InternalReportMetaHeader items={metaItems} />

      <div className="h-0 w-full border-t border-[#E5E5E5]" />

      <div className="flex w-full flex-col gap-2">
        <div className="flex w-full items-center justify-between">
          <p className="text-base font-semibold leading-6 text-[#374151]">Overview</p>
          <MonthYearPicker value={selectedPeriod} onChange={handlePeriodChange} />
        </div>
        {isFetching ? (
          <InternalReportTableShimmer columns={8} rows={6} />
        ) : (
          <InternalReportTable<BorrowalVsNonBorrowalTableRow>
            columns={overviewColumns}
            mergedHeadersSets={borrowalVsNonBorrowalMergedHeadersSets}
            rows={overviewRows}
          />
        )}
      </div>

      {isFetching ? (
        <InternalReportTableShimmer columns={1} rows={2} />
      ) : (
        <InternalReportTable<MetricRow>
          columns={metricColumns}
          rows={metrics.map((metric) => ({ key: metric.label, data: metric }))}
        />
      )}
    </div>
  );
};

export default BorrowalVsNonBorrowalReport;
